import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * WHY THE PERSON IS SIGNED OUT, for the one line the sign-in screen says about
 * it (copy-1, Founder-approved copy 2026-10-06).
 *
 *  - `session-ended`: UNEXPECTED. The session-end owner (App SessionEndOwner,
 *    Book IdentityProvider via routeAfterSessionEnd) ended it after Backend's
 *    SESSION_REVOKED or the Firebase SDK calling the identity invalid.
 *    "Your session ended. Sign in to continue."
 *  - `signed-out`: DELIBERATE, the person's own sign-out control (App /login
 *    "Use another account", Book create "Use another account", Book's step
 *    footer "Sign out", whose line shows on the service step).
 *    "You're signed out." and no prompt to sign back in.
 *
 * IN MEMORY ONLY, the same reasoning as App's features/auth/pendingEmail.ts:
 * a module variable in the running bundle. Never the URL or query, never
 * router location state (React Router writes that into `history.state`,
 * which the browser keeps across a reload), never storage, never a token or
 * UID. A reload or a new tab starts with nothing, so a fresh visit or a
 * reload never shows it.
 *
 * SHOWN ONCE. `useSignOutNotice` takes the reason as its screen displays it
 * (or only the one reason it was asked for, leaving the other pending);
 * the next screen to mount (or a remount of the same one) finds nothing.
 *
 * A DELIBERATE SIGN-OUT WINS. Once the person has chosen to sign out, a late
 * session end for the same sign-in (a request that was already in flight) is
 * not news, and never replaces "You're signed out." with "Your session
 * ended". Signing in again (`noteSignedIn`) clears both.
 */
export type SignOutNotice = "session-ended" | "signed-out";

let pending: SignOutNotice | null = null;
let deliberate = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

/** Record why the person was just signed out. */
export function announceSignOut(kind: SignOutNotice): void {
  if (kind === "session-ended" && deliberate) return;
  if (kind === "signed-out") deliberate = true;
  pending = kind;
  emit();
}

/** A credentialed identity arrived: whatever ended before is no longer news. */
export function noteSignedIn(): void {
  deliberate = false;
  if (pending === null) return;
  pending = null;
  emit();
}

function take(): void {
  if (pending === null) return;
  pending = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = (): SignOutNotice | null => pending;
const serverSnapshot = (): SignOutNotice | null => null;

/**
 * The reason to show on this screen, or null. Subscribed, so a screen that is
 * already mounted when the sign-out lands (App /login after "Use another
 * account") picks it up. Taken in an effect, so the value first renders into a
 * live region that is already in the DOM and is announced, and so StrictMode's
 * second render and effect pass see the same answer.
 */
export function useSignOutNotice(only?: SignOutNotice): SignOutNotice | null {
  const live = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  const [shown, setShown] = useState<SignOutNotice | null>(null);
  useEffect(() => {
    if (live === null) return;
    // A screen that says only one of the lines leaves the other for the
    // screen it belongs to (Book's service step and "signed-out").
    if (only !== undefined && live !== only) return;
    setShown(live);
    take();
  }, [live, only]);
  return shown;
}

/** Test seam: forget everything, so one test cannot leak into the next. */
export function resetSignOutNoticeForTests(): void {
  pending = null;
  deliberate = false;
  listeners.clear();
}
